import React, { useContext, useEffect, useState } from 'react';
import { Box, ButtonGroup, Flex, useColorModeValue } from '@chakra-ui/react';
import LinkButton from '../../../components/custom/LinkButton';
import AddButton from '../../../components/custom/AddButton';
import { UseApi } from '../../../libraries/UseApi';
import { UserInfoContext } from '../../../provider/UserInfoProvider';
import { UseConstant } from '../../../libraries/UseConstant';
import { UseMessage } from '../../../libraries/UseMessage';
import DeleteButton from '../../../components/custom/DeleteButton';
import Loading from '../../../components/common/Loading';
import NoData from '../../../components/common/NoData';
import SearchBar from '../../../components/search/SearchBar';
import PageHeader from '../../../components/common/PageHeader';
import CustomTable from '../../../components/custom/CustomTable';

export default function Technologies() {
    const [isProcessing, setIsProcessing] = useState(false);

    const {token} = useContext(UserInfoContext);
    const {constant} = UseConstant();
    const {Message} = UseMessage();
    const {TECHNOLOGY_LIST} = constant;

    const { deleteApi, getApi } = UseApi();

    const cardBg = useColorModeValue('whiteAlpha.800', 'whiteAlpha.100');

    // ページ番号
    const [currentPageNo, setCurrentPageNo] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    // 検索パラメータ
    const [searchParams, setSearchParams] = useState({});

    const [technologies, setTechnologies] = useState([]);

    // 検索データ
    const searchFieldsInitial = [
        {
            name: 'search_technology',
            label: '技術',
            type: 'text',
        },
        {
            name: 'search_stack',
            label: '技術分類',
            type: 'dropdown',
            options: [],
        },
    ];
    const [searchFields, setSearchFields] = useState(searchFieldsInitial);

    
    // 検索処理
    const getTechnologies = () => {
        // 処置中チェック
        if (isProcessing) {
            // 処理中の場合、以降の処理を実施しない
            return;
        }
        // 処理中フラグをONにする
        setIsProcessing(true);

        // 検索パラメータ
        let paging = {
            limit: TECHNOLOGY_LIST,
            offset: currentPageNo - 1
        };
        let params = { ...paging, ...searchParams };

        // 未受検者検索API
        getApi(
            `${process.env.REACT_APP_API_URL}/technology`, params, { token: token },
            (response) => {
                // 成功時
                if (response.isSuccess === false) {
                    // error
                    alert(Message.Common.ERROR);
                    // 処理中フラグを元に戻す
                    setIsProcessing(false);
                    return;
                }

                setTechnologies(response.data?.technologies);
                setTotalPages(Math.ceil(response.data?.total / paging?.limit));
                // 処理中フラグを元に戻す
                setIsProcessing(false);
            },
            () => {
                // 異常時
                // 処理中フラグをOFFにする
                setIsProcessing(false);
            }
        );
    };

    const handleDelete = (id) => {
        deleteApi(
            `${process.env.REACT_APP_API_URL}/technology/${id}`, { token: token },
            (response) => {
                // レスポンスチェック
                if (response.isSuccess === false) {
                    // 入力内容を確認してください。
                    //alert(Message.Common.CONFIRM_INPUT);
                    return;
                }
                getTechnologies();
            }
        );        
    };

    useEffect(() => {
        getTechnologies();
        getStackOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[currentPageNo]);

    const handleSearch = () => {
        setCurrentPageNo(1); 
        getTechnologies();
    };

    const getStackOptions = () => {
        getApi(
            `${process.env.REACT_APP_API_URL}/technology/stack_options/get`, {}, { token: token },
            (response) => {
                // 成功時
                if (response.isSuccess === false) {
                    // error
                    alert(Message.Common.ERROR);
                    // 処理中フラグを元に戻す
                    setIsProcessing(false);
                    return;
                }

                // Transform assertedArr into options format
                const newOptions = response.data.map(item => ({
                    label: item.stack,
                    value: item.stack
                }));

                const updatedSearchFields = [...searchFields].map(field => {
                    if (field.name === 'search_stack') {
                        return {
                            ...field,
                            options: [
                                ...(field.options || []),
                                ...newOptions
                            ]
                        };
                    }
                    return field;
                });

                setSearchFields(updatedSearchFields);
                
            },
            () => {
                // 異常時
            }
        );
    }

    const handlePageChange = (page) => {
        setCurrentPageNo(page);
    }

    // Define columns
    const columns = [
        { header: '技術', accessor: 'technology' },
        { header: '技術分類', accessor: 'stack' },
    ];

    // Define actions
    const renderActions = (row) => (
        <>
            <LinkButton
                buttonText={'編集'}
                buttonUrl={`/technology/edit/${row.id}`}
            >
            </LinkButton>
            <DeleteButton 
                colorScheme='red'
                handleDelete={handleDelete}
                id={row?.id}
            >
            </DeleteButton>
        </>
    );

    return (
        <Box
            px={{ base: 2, md: 5 }}
            py={4}
            bgGradient="linear(to-br, orange.50, white)"
            minH="100vh"
            w="full"
        >
            {/* Page Header */}
            <Flex
                justify="space-between"
                align="center"
                mb={5}
                flexWrap="wrap"
                gap={4}
            >
                <PageHeader headerTitle={'技術一覧'} />
                <ButtonGroup>
                    <AddButton buttonText="新規登録" buttonUrl="/add-technology" />
                    <LinkButton buttonText="戻る" buttonUrl="/admin-dashboard" />
                </ButtonGroup>
            </Flex>

            {/* Gorgeous Container */}
            <Box
                bg={cardBg}
                boxShadow="xl"
                backdropFilter="blur(10px)"
                p={{ base: 4, md: 8 }}
            >
                {/* Search */}
                <Box mb={6}>
                    <SearchBar
                        searchFields={searchFields}
                        searchParams={searchParams}
                        setSearchParams={setSearchParams}
                        onSearch={handleSearch}
                    />
                </Box>

                {/* Content */}
                {isProcessing ? (
                    <Loading />
                ) : technologies.length === 0 ? (
                    <NoData />
                ) : (
                    <CustomTable
                        columns={columns}
                        data={technologies}
                        renderActions={renderActions}
                        paginationProps={{
                            currentPage: currentPageNo,
                            totalPages: totalPages,
                            onPageChange: handlePageChange
                        }}
                    />
                )}
            </Box>
        </Box> 
    )
}
